<script>
import messages from '@/mixins/messages'
import stripe, { elements } from '@/stripe'
import { moneyFormatFromPositiveInteger } from '@/util'
import TermsOfServiceLink from './TermsOfServiceLink.vue'
import ChargeResource from '@/resources/charge'

export default {
    name: 'PaymentMethod',
    components: {
        TermsOfServiceLink
    },
    mixins: [messages],
    data() {
        return {
            userCardDetails: {},
            loading: true,
            addingCard: false,
            elem: null,
            activePaymentTab: 'default'
        }
    },
    watch: {
        loading() {
            this.$emit('loading', this.loading)
        }
    },
    props: {
        userResource: {
            type: Object,
            required: true
        },
        colsSm: {
            default: 3
        },
        colsLg: {
            default: 4
        }
    },
    methods: {
        moneyFormatFromPositiveInteger,
        async addCard() {
            this.addingCard = true
            let result = await stripe.createToken(this.elem)
            if (result.error) {
                this.addMessage('danger', result.error.message)
                this.$emit('error', result.error)
                this.addingCard = false
            } else {
                try {
                    this.userCardDetails = await this.userResource.addCard(result.token.id)
                    this.$analytics.track('added_credit_card')
                    this.elem.destroy()
                    this.elem = null
                    this.$emit('retrieve', this.userCardDetails)
                    this.$emit('success', {
                        userResource: this.userResource,
                        card: this.userCardDetails
                    })

                    this.$nextTick(() => this.captureOutstandingItems())
                } catch(e) {
                    throw this.axiosExtract(e)
                } finally {
                    this.addingCard = false
                }
            }
        },
        cardElementStyle() {
            const root = getComputedStyle(document.documentElement)
            const token = (name, fallback) => (root.getPropertyValue(name) || '').trim() || fallback
            const isDark = document.documentElement.getAttribute('data-theme') === 'dark'
            const ink = token('--ink', isDark ? '#e8e8e8' : '#1c1d21')
            const muted = token('--muted', isDark ? '#9a9a9a' : '#6a6c74')
            return {
                base: {
                    color: ink,
                    iconColor: ink,
                    '::placeholder': { color: muted }
                },
                invalid: {
                    color: token('--danger', '#bf1e00'),
                    iconColor: token('--danger', '#bf1e00')
                }
            }
        },
        enableCardAdd() {
            this.userCardDetails = {}

            this.elem = elements.create('card', { style: this.cardElementStyle() })

            setTimeout(() => {
                if(this.$refs.card) {
                    this.$refs.card.className = 'form-control add-card'
                    this.elem.mount(this.$refs.card)
                    this.elem.on('complete', () => {
                        // Do nothing
                    })
                }
            }, 500)
        },
        async captureOutstandingItems() {
            let balance_cents = await ChargeResource.getOutstandingBalance()

            if(balance_cents > 0) {
                await ChargeResource.captureOutstanding()
            }
        }
    },
    async mounted() {
        this.loading = true
        this.userCardDetails = await this.userResource.getCard()
        this.$emit('retrieve', this.userCardDetails)
        this.loading = false

        if (!Object.keys(this.userCardDetails).length) {
            this.enableCardAdd()
        }
    },
    beforeDestroy() {
        if(this.elem) {
            this.elem.destroy()
        }
    }
}
</script>

<template>
    <div>
        <div v-if="loading" class="text-center">
            Wird geladen...
        </div>

        <div v-else>
            <div v-if="Object.keys(userCardDetails).length === 0">
                <b-form-group
                    label="Eine Kreditkarte hinzufügen"
                    :label-cols-sm="colsSm"
                    :label-cols-lg="colsLg">
                    <div ref="card"></div>
                </b-form-group>
                <b-form-group
                    :label-cols-sm="colsSm"
                    :label-cols-lg="colsLg">
                    <img alt="" height="25" src="@/assets/ccards.png">
                </b-form-group>
                <slot />
                <slot name="tos">
                    <b-form-group
                        :label-cols-sm="colsSm"
                        :label-cols-lg="colsLg">
                        <small class="text-muted">
                            <p class="mb-0"><terms-of-service-link /></p>
                        </small>
                    </b-form-group>
                </slot>
                <slot name="before-submit-button" />
                <messages />
                <b-form-group
                    :label-cols-sm="colsSm"
                    :label-cols-lg="colsLg">
                    <b-button variant="primary" @click="addCard()" :disabled="addingCard">Zahlungsmethode hinzufügen</b-button>
                    <slot name="after-submit-button-text" />
                </b-form-group>
                <slot name="after-submit-button" />
            </div>

            <div v-else>
                <div>
                    <b-icon icon="credit-card" /> {{ userCardDetails.brand }} **** **** **** {{ userCardDetails.last4 }}<br />
                    {{ userCardDetails.exp_month }} / {{ userCardDetails.exp_year }} 
                    <span v-if="userCardDetails.address_zip"><br />
                        Postleitzahl: {{ userCardDetails.address_zip }}
                    </span>
                </div>
                <b-button variant="outline-primary" @click="enableCardAdd()" class="mr-2 mt-2">Zahlungsmethode aktualisieren</b-button>
                <slot name="after-update-payment-method-link" />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.add-card {
    padding-top: 10px;
}
</style>
